import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Phone, Mail, MapPin, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/quotes/stats`),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Admin · Sicilia Traslochi";
    load();
  }, []);

  return (
    <div className="min-h-screen bg-brand-surface" data-testid="admin-page">
      <header className="bg-white border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SITE.logoUrl} alt="logo" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display font-extrabold text-brand-ink tracking-tight">Admin · Preventivi</div>
              <div className="text-xs text-brand-body">Richieste ricevute dal sito</div>
            </div>
          </div>
          <Button onClick={load} variant="outline" className="rounded-full" data-testid="admin-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-10">
            <StatCard label="Totale" value={stats.total} highlight />
            {Object.entries(stats.by_service).map(([k, v]) => (
              <StatCard key={k} label={v.label} value={v.count} />
            ))}
          </div>
        )}

        <div className="space-y-3">
          {quotes.length === 0 && !loading && (
            <div className="bg-white border border-brand-line rounded-2xl p-10 text-center text-brand-body">
              Nessuna richiesta ancora. Quando arriveranno preventivi dal sito, li vedrai qui.
            </div>
          )}
          {quotes.map((q) => (
            <article
              key={q.id}
              data-testid={`quote-row-${q.id}`}
              className="bg-white border border-brand-line rounded-2xl p-6 hover:shadow-brand transition-shadow"
            >
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold text-xl text-brand-ink">{q.name}</h3>
                    <Badge className="bg-brand-primary/10 text-brand-primary border-brand-primary/20">
                      {q.service_label}
                    </Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-brand-body">
                    <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 hover:text-brand-primary">
                      <Phone className="w-3.5 h-3.5" />
                      {q.phone}
                    </a>
                    {q.email && (
                      <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 hover:text-brand-primary">
                        <Mail className="w-3.5 h-3.5" />
                        {q.email}
                      </a>
                    )}
                    {(q.origin || q.destination) && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {q.origin || "—"} → {q.destination || "—"}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(q.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  {q.message && (
                    <p className="mt-3 text-brand-ink bg-brand-surface rounded-xl p-4 text-sm leading-relaxed border border-brand-line">
                      {q.message}
                    </p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      className={`p-5 rounded-2xl border ${
        highlight ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"
      }`}
    >
      <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold ${highlight ? "text-white/80" : "text-brand-body"}`}>
        {label}
      </div>
      <div className={`font-display font-extrabold text-3xl mt-1 ${highlight ? "text-white" : "text-brand-ink"}`}>
        {value}
      </div>
    </div>
  );
}
